// © 2013 - 2023 Tinder, Inc., ALL RIGHTS RESERVED

import React, { memo, forwardRef, useCallback } from 'react';
import { cx } from '@linaria/core';

import styles from './styles';
import { RadioInputProps } from './types';

import { useKeyboardFocus } from '../../utils/hooks';

import { IconSelectionControlsRadioButtonSelected } from '../../icons';

const RadioButton: React.ForwardRefRenderFunction<HTMLInputElement, RadioInputProps> = (
  {
    className,
    children,
    focused,
    disabled = false,
    readOnly = false,
    checked,
    onBlur,
    onChange,
    onFocus,
    value,
    accentColor,
    ...restProps
  },
  ref
) => {
  const [isFocused, , setIsFocused] = useKeyboardFocus(false);

  const handleFocus = useCallback<React.FocusEventHandler<HTMLInputElement>>(
    (e) => {
      setIsFocused(true);
      if (typeof onFocus === 'function') {
        onFocus(e);
      }
    },
    [onFocus, setIsFocused]
  );

  const handleBlur = useCallback<React.FocusEventHandler<HTMLInputElement>>(
    (e) => {
      setIsFocused(false);
      if (typeof onBlur === 'function') {
        onBlur(e);
      }
    },
    [onBlur, setIsFocused]
  );

  const handleChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      if (!e.currentTarget.disabled && !e.currentTarget.readOnly) {
        onChange(e, e.currentTarget.checked);
      }
    },
    [onChange]
  );

  return (
    <div>
      <span
        data-focused={focused || isFocused ? '' : undefined}
        data-disabled={disabled ? '' : undefined}
        data-readonly={readOnly ? '' : undefined}
        data-checked={checked ? '' : undefined}
        data-testid="toggle-radio"
        style={
          accentColor
            ? ({ '--tui-radio-button-accent-color': accentColor } as React.CSSProperties)
            : undefined
        }
        className={cx(className, styles.container)}
      >
        <input
          {...restProps}
          checked={checked}
          disabled={disabled}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={onChange && handleChange}
          readOnly={readOnly}
          ref={ref}
          type="radio"
          value={value}
        />
        <IconSelectionControlsRadioButtonSelected className={styles.radio} />
        <span className={styles.overlay} />
      </span>
    </div>
  );
};

export default memo(forwardRef<HTMLInputElement, RadioInputProps>(RadioButton));
